import axios from 'axios';
import { extractOrgBlock, reportOrgBlock } from '@/lib/orgBlock';
import { hasSessionCookie } from '@/lib/sessionHint';
import { CLIENT_ID } from '@/lib/clientId';

// Same-origin privzeto (transport-safe): ista zgradena slika dela na localhost,
// stagingu in produ, ker nginx na vsakem serverju posreduje /api in /ws na lasten
// backend. `VITE_API_URL` ostane opcijski override (lokalni dev → localhost:8000).
let API_URL = import.meta.env.VITE_API_URL || '/api/v1';
if (!API_URL.endsWith('/')) {
  API_URL += '/';
}

// Base origin without /api/vX suffix — use this for media URLs (avatars, logos).
// Pri same-origin (relativni API_URL) postane prazen niz → media so same-origin relativni.
export const BASE_URL = API_URL.replace(/\/api\/v\d+\/$/, '');

// WebSocket base — same-origin izpeljan iz protokola/hosta (wss na https), z
// opcijskim `VITE_WS_URL` override. Brez sledilnega `/` (hooki dodajo pot).
export const WS_BASE =
  import.meta.env.VITE_WS_URL ||
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (err: unknown) => void;
}> = [];

// Registered by authStore to clear React state on unrecoverable auth failure
let _onAuthFailure: (() => void) | null = null;
export const registerAuthFailureHandler = (fn: () => void) => {
  _onAuthFailure = fn;
};

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleAuthFailure = () => {
  if (_onAuthFailure) _onAuthFailure();
};

/**
 * T2376 — ponovna prijava nad odprto stranjo namesto preusmeritve na `/login`.
 *
 * Rokovalnik registrira okno za ponovno prijavo (`SessionExpiredDialog`). Vrne
 * obljubo, ki se izpolni, ko se **isti** uporabnik prijavi znova (zahteve se
 * nato ponovijo — tudi neshranjen vnos), ali zavrne ob preklicu. Vrne `null`,
 * kadar ponovna prijava ni smiselna (nihče ni bil prijavljen, npr. zagonski
 * `auth/me/`) — takrat ostane staro vedenje (`clearAuth`).
 */
type ReauthHandler = () => Promise<void> | null;
let _onReauthNeeded: ReauthHandler | null = null;
export const registerReauthHandler = (fn: ReauthHandler | null) => {
  _onReauthNeeded = fn;
};

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(';').shift() ?? null;
  return null;
}

// Add CSRF token header for state-changing requests (Double Submit Cookie pattern)
api.interceptors.request.use(
  (config) => {
    // T2333: zavihek, ki je sprožil spremembo — zaledje ga vrne v WS dogodku,
    // da ta zavihek prezre svoj odmev (drugi zavihki istega uporabnika ne).
    config.headers['X-Client-Id'] = CLIENT_ID;
    const method = config.method?.toUpperCase() ?? '';
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      const csrfToken = getCookie('csrf_token');
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    /**
     * T1736 — `FormData` ne sme skozi s privzeto glavo `application/json`.
     *
     * Instanca ima `Content-Type: application/json` med **privzetki**, zato ga
     * axios ob `FormData` ne zamenja sam: glava je izrecno nastavljena in jo
     * axios spoštuje. Zaledje tak zahtevek zavrne s **415** (»Nepodprt medijski
     * tip "application/json" v zahtevku.«) — brez enega samega zapisa v
     * dnevniku odjemalca, ker je odgovor sintaktično veljaven.
     *
     * 🔴 **To ni bila posamična napaka, ampak razred.** Tri klicna mesta so
     * glavo pozabila prepisati — uvoz v knjižnico (`useLibraryImportUpload`),
     * nalaganje potrdila in nalaganje slike portfelja na tržnici — druga tri
     * (`attachmentsApi`, `bannersApi`, `ifcApi`) so si vsako po svoje pomagala.
     * Popravek zato stoji **tu in ne na klicnih mestih**: vsako naslednje
     * nalaganje datoteke je pravilno, ne da bi kdo pomnil to pravilo.
     *
     * Odstranimo **samo privzetek** — kdor glavo nastavi sam (npr. `ifcApi` z
     * `undefined`, `attachmentsApi` z `multipart/form-data`), obdrži svojo.
     * Brisanje glave prepusti njeno sestavo brskalniku, ki edini pozna mejo
     * (`boundary`) sestavljenega telesa; ročno zapisan `multipart/form-data`
     * brez meje bi telo naredil neberljivo.
     */
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const glava = config.headers['Content-Type'];
      if (typeof glava === 'string' && glava.startsWith('application/json')) {
        delete config.headers['Content-Type'];
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // T1192: zaklep organizacije (403 + ORGANIZATION_SUSPENDED / _CLOSED) se
    // prestreza TU in nikjer drugje. Ob zaklepu je zaprt praktično ves API,
    // zato bi obravnava po posameznih zaslonih pomenila, da uporabnik vidi
    // naključno napako tiste poizvedbe, ki je slučajno padla prva. Napako
    // vseeno zavrnemo naprej — klicatelji svojih `onError` poti ne izgubijo.
    const orgBlock = extractOrgBlock(error);
    if (orgBlock) {
      reportOrgBlock(orgBlock);
    }

    // Public/unauthenticated auth endpoints: a 401 here must NOT trigger a
    // token refresh. A stale access_token cookie (e.g. left over after a DB
    // reset) would otherwise bounce 401 → refresh, and the refresh — not
    // CSRF-exempt — fails with "CSRF preverba ni uspela.", masking the real
    // flow on the login/register pages. The backend now treats such stale
    // cookies as anonymous (T915); this keeps the client from a needless
    // refresh round-trip on those pages too.
    const NO_REFRESH_AUTH_PATHS = [
      'auth/login',
      // T2816: 401 tu pomeni potekel vmesni žeton 2FA, ne potekle seje.
      'auth/2fa/verify',
      'auth/register',
      'auth/waitlist',
      'auth/join-request',
      'auth/google',
      'auth/microsoft',
      'auth/password-reset',
      'auth/password-confirm',
      'auth/email-verify',
      'auth/email-confirm',
      'auth/gdpr-consent',
      'auth/token/refresh',
      'auth/logout',
    ];
    const isAuthEndpoint = NO_REFRESH_AUTH_PATHS.some((p) =>
      originalRequest.url?.includes(p)
    );

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // T2376: med odprtim oknom za ponovno prijavo `isRefreshing` ostane
      // `true`, zato se vsaka nova 401 zahteva postavi v isto vrsto in se
      // ponovi po prijavi — okno se ne odpre dvakrat, refresh se ne ponavlja.
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // T1858: brez piškotka seje osvežitvenega piškotka ni — `token/refresh/`
        // bi vrnil 400 in samo dodal rdečo vrstico v konzolo. Izid je isti kot
        // ob padlem refreshu: seja ne obstaja.
        if (!hasSessionCookie()) throw error;
        // Browser sends refresh_token cookie automatically (withCredentials: true)
        // T2718: goli `axios.post` gre mimo prestreznika, ki doda CSRF glavo.
        // Zaledje CSRF zahteva, dokler piškotek `access_token` še živi (poteče
        // po uri brskalnika, JWT po uri strežnika) — brez glave je refresh v
        // tem oknu padel s 403 in odprl lažno okno »Seja je potekla«.
        const csrfToken = getCookie('csrf_token');
        await axios.post(
          `${API_URL}auth/token/refresh/`,
          {},
          {
            withCredentials: true,
            headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined,
          }
        );

        processQueue(null, null);
        isRefreshing = false;

        return api(originalRequest);
      } catch (err) {
        // T2376: seja je potekla med delom — okno za ponovno prijavo nad
        // stranjo; zahteve počakajo in se po prijavi ponovijo.
        const reauth = _onReauthNeeded ? _onReauthNeeded() : null;
        if (reauth) {
          try {
            await reauth;
          } catch {
            processQueue(error, null);
            isRefreshing = false;
            return Promise.reject(error);
          }
          processQueue(null, null);
          isRefreshing = false;
          return api(originalRequest);
        }
        processQueue(err, null);
        isRefreshing = false;
        handleAuthFailure();
        return Promise.reject(err);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
